import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Switch, toast } from "@sagot/ui";
import { useFieldArray, useFormContext } from "react-hook-form";
import { LIMITS, WEEKDAYS, WEEKDAY_TARGETS, copyToWeekdays, suggestRange, type AvailabilityValues, type Weekday } from "./availability-model";

export const HOURS_SECTION_ID = "availability-hours";

function DayRow({ day, disabled }: { day: Weekday; disabled: boolean }) {
  const { t } = useTranslation();
  const { control, register, getValues, setValue, formState } = useFormContext<AvailabilityValues>();
  const name = `weekly_hours.${day}` as const;
  const { fields, append, remove, replace } = useFieldArray({ control, name });
  const label = t(`build.actions.availability.hours.days.${day}`);
  const dayErrors = formState.errors.weekly_hours?.[day];
  const messages = [dayErrors?.root?.message, ...fields.flatMap((_, index) => [dayErrors?.[index]?.open?.message, dayErrors?.[index]?.close?.message])];
  const problems = [...new Set(messages.filter((message): message is string => Boolean(message)))];
  const copy = () => {
    const next = copyToWeekdays(getValues("weekly_hours"), day);
    for (const target of WEEKDAY_TARGETS) if (target !== day) setValue(`weekly_hours.${target}`, next[target], { shouldDirty: true });
    toast.success(t("build.actions.availability.hours.copied", { day: label }));
  };
  return (
    <div role="group" aria-label={label} className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-[9rem_1fr] sm:gap-4" data-testid={`hours-${day}`}>
      <div className="flex items-center gap-3">
        <Switch
          checked={fields.length > 0}
          disabled={disabled}
          aria-label={t("build.actions.availability.hours.openToggle", { day: label })}
          onCheckedChange={(on) => {
            if (on) append(suggestRange([]));
            else replace([]);
          }}
        />
        <span className={fields.length > 0 ? "text-sm font-semibold" : "text-sm font-semibold text-fg-muted"}>{label}</span>
      </div>
      <div className="min-w-0 space-y-2">
        {fields.length === 0 ? <p className="py-1.5 text-sm text-fg-subtle">{t("build.actions.availability.hours.closed")}</p> : null}
        {fields.map((field, index) => {
          const invalid = Boolean(dayErrors?.[index] ?? dayErrors?.root);
          const values = { day: label, number: index + 1 };
          return (
            <div key={field.id} className="flex flex-wrap items-center gap-2">
              <Input type="time" className="h-9 w-32" disabled={disabled} invalid={invalid} aria-label={t("build.actions.availability.hours.opens", values)} {...register(`${name}.${index}.open`)} />
              <span aria-hidden="true" className="text-fg-subtle">–</span>
              <Input type="time" className="h-9 w-32" disabled={disabled} invalid={invalid} aria-label={t("build.actions.availability.hours.closes", values)} {...register(`${name}.${index}.close`)} />
              {disabled ? null : (
                <Button type="button" variant="ghost" size="sm" iconOnly aria-label={t("build.actions.availability.hours.remove", values)} onClick={() => { remove(index); }}>
                  <Icon name="x" />
                </Button>
              )}
            </div>
          );
        })}
        {problems.map((problem) => (
          <p key={problem} className="text-xs font-semibold text-danger" role="alert">{problem}</p>
        ))}
        {disabled ? null : (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" size="sm" disabled={fields.length >= LIMITS.windowsPerDay} aria-label={t("build.actions.availability.hours.addLabel", { day: label })} onClick={() => { append(suggestRange(getValues(name))); }}>
              <Icon name="plus" />
              {t("build.actions.availability.hours.add")}
            </Button>
            {fields.length > 0 ? (
              <Button type="button" variant="ghost" size="sm" aria-label={t("build.actions.availability.hours.copyLabel", { day: label })} onClick={copy}>
                <Icon name="copy" />
                {t("build.actions.availability.hours.copy")}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/** Seven day rows, each with up to four `HH:MM` ranges; a day without ranges is closed. */
export function HoursEditor({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { formState } = useFormContext<AvailabilityValues>();
  const noOpenDay = formState.errors.weekly_hours?.root?.message;
  return (
    <section id={HOURS_SECTION_ID} tabIndex={-1} aria-labelledby="availability-hours-title" className="outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <h3 id="availability-hours-title" className="text-sm font-bold">{t("build.actions.availability.hours.title")}</h3>
      <p className="mt-0.5 text-xs text-fg-subtle">{t("build.actions.availability.hours.hint", { count: LIMITS.windowsPerDay })}</p>
      {noOpenDay ? <p className="mt-2 text-xs font-semibold text-danger" role="alert">{noOpenDay}</p> : null}
      <div className="mt-2 divide-y divide-line">
        {WEEKDAYS.map((day) => <DayRow key={day} day={day} disabled={disabled} />)}
      </div>
    </section>
  );
}
